// `configureCallsData` — the injected-config seam for the SECOND TRANSPORT
// CLASS, and only for it.
//
// Four of this package's five api modules resolve transport from the
// `@heidi/api/client` registry (E5) and need nothing from here. The fifth,
// `api/agent-chat.ts`, cannot: it streams SSE and it talks to a separate chat
// service whose base URL is an app env read. Both of those are app-owned by
// rule (F5/G1 — a package reads no env and imports no `@/` module), so they
// cross as ports and the bodies stay byte-identical either side of the move.
//
// THREE PORTS, NOT THE TWO THE SEAM WAS SCOPED FOR, and the third is disclosed
// rather than folded in quietly. `getCallsChatApiBaseUrl` and
// `fetchEventSource` were the two named at scoping. Measuring the quartet's
// bodies before moving them found a third app-owned input that scoping had not
// seen: all three fetch calls send `getV2StreamHeaders()`, which reads the
// auth-token cache, the workspace atom and the temp-user id. It is NOT this
// family's function — the family record says so explicitly, and it is the
// generic v2 stream-header builder the non-calls hub suite tests — so it stays
// where it is and arrives here as `getStreamHeaders`. Inventing a fourth
// carve for it would have moved app surface the family does not own.
//
// The seam is born with exactly the fields the moving code uses. An unused
// field is a promise the package has not yet had to keep (the commerce rule).

/**
 * One decoded SSE frame, structurally identical to the wire type the app's
 * client emits.
 *
 * Declared here rather than imported so `/data` takes no dependency on a
 * transport library it does not construct — the app owns the SSE client and
 * passes it in. `id` and `event` are required because they are required on the
 * wire type; the app's real callback is contextually typed by this shape, and
 * `simulateAgentStream`'s one consumer reads `ev.event` and `ev.data`.
 */
export interface CallsEventSourceMessage {
  id: string;
  event: string;
  data: string;
  retry?: number;
}

/**
 * The request the package hands to the app's SSE client.
 *
 * A strict subset of what the app client accepts, spelled out here so the
 * package states what it needs rather than naming an app type. `path` carries
 * NO leading slash and that is load-bearing, not style — the app client
 * concatenates it onto a base URL that already ends in one.
 */
export interface CallsEventSourceRequest {
  endPointVersion?: 'v1' | 'v2' | 'v2-1';
  path: string;
  requestBody?: object | null;
  abortController: AbortController;
  onmessage?: (ev: CallsEventSourceMessage) => void;
  onclose?: () => void;
}

export interface CallsDataConfig {
  /**
   * The chat service's base URL, resolved fresh on every call.
   *
   * A THUNK rather than a string because the app's resolution reads
   * `process.env.NEXT_PUBLIC_CALLS_API_BASE_URL`, `NEXT_PUBLIC_ENVIRONMENT`
   * and the REGIONAL domain — and the region can change after bootstrap, so a
   * value captured at wiring time would pin the user to whichever region
   * happened to be resolved first. The package never learns any of that; it
   * learns that someone app-side can answer the question when asked.
   */
  getCallsChatApiBaseUrl: () => string;
  /**
   * The headers the app's v2 axios interceptor would have added, for the raw
   * `fetch` calls that cannot go through axios.
   *
   * Injected for the same reason as the base URL and one more: it reads the
   * auth-token cache and the workspace atom, so resolving it eagerly would
   * freeze a token that rotates. Called per request, exactly where the hub
   * called it.
   */
  getStreamHeaders: () => Record<string, string>;
  /**
   * The app's SSE client. Returns the concatenated stream text on close and
   * rejects on abort or error — the contract `simulateAgentStream`'s caller
   * already relies on.
   */
  fetchEventSource: (request: CallsEventSourceRequest) => Promise<string>;
}

let config: CallsDataConfig | null = null;

export const configureCallsData = (next: CallsDataConfig): void => {
  config = next;
};

/** Fails loudly rather than silently degrading — the workspace/commerce rule. */
export const getCallsDataConfig = (): CallsDataConfig => {
  if (!config) {
    throw new Error(
      '[calls/data] not configured — see src/composition/hosts/calls/calls-data-wiring.ts'
    );
  }
  return config;
};
